/**
 * PowersTable Component
 * Displays powers in a report - mobile cards / desktop table
 * With edit/delete/proof actions and totals
 */

import {
  PencilIcon,
  TrashIcon,
  CameraIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { OathEmptyState } from '../../../shared/OathUI';
import PowerCard from './PowerCard';
import { formatCurrency, formatDate, useIsMobile } from '../utils';

// Capitalize first letter of a string
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const PowersTable = ({
  powers = [],
  onEditPower,
  onDeletePower,
  onUploadProof,
  isSubmitted = false,
  reportStatus = 'DRAFT',
  agencyId,
}) => {
  const isMobile = useIsMobile();

  // Check if any power is from South Carolina (show SC Premium column only if needed)
  const hasSCPowers = powers.some(p => p.stateCode === 'SC' || (p.scamount && p.scamount > 0));

  // Calculate totals - always use original values (commission for NET, buf for BUF)
  const calculateTotal = (field) => {
    return powers.reduce((sum, power) => sum + (parseFloat(power[field]) || 0), 0);
  };

  // Calculate NET total - always use original commission value
  const calculateNetTotal = () => {
    return powers.reduce((sum, power) => {
      return sum + (parseFloat(power.commission) || 0);
    }, 0);
  };

  // Calculate BUF total - always use original buf value
  const calculateBufTotal = () => {
    return powers.reduce((sum, power) => {
      return sum + (parseFloat(power.buf) || 0);
    }, 0);
  };

  // Empty state
  if (!powers || powers.length === 0) {
    return (
      <OathEmptyState
        icon={CameraIcon}
        title="No powers added"
        message="No powers added to this report yet. Click 'Add Power' to get started."
      />
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-3">
        {powers.map((power) => (
          <PowerCard
            key={power.powerReportPowerId}
            power={power}
            onEdit={onEditPower}
            onDelete={onDeletePower}
            onUploadProof={onUploadProof}
            isSubmitted={isSubmitted}
            reportStatus={reportStatus}
            agencyId={agencyId}
          />
        ))}

        {/* Totals Card */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-gray-300 dark:border-gray-600 p-4">
          <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
            TOTALS ({powers.length} {powers.length === 1 ? 'Power' : 'Powers'})
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                ${formatCurrency(calculateTotal('powerAmount'))}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Premium</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                ${formatCurrency(calculateTotal('premium'))}
              </p>
            </div>
            {hasSCPowers && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">SC Premium</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  ${formatCurrency(calculateTotal('scamount'))}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">NET</p>
              <p className="text-sm font-bold text-primary dark:text-primary-light">
                ${formatCurrency(calculateNetTotal())}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">BUF</p>
              <p className="text-sm font-bold text-primary dark:text-primary-light">
                ${formatCurrency(calculateBufTotal())}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop table view - all columns with optimized widths
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
        <colgroup>
          <col style={{ width: '100px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '90px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '70px' }} />
          {hasSCPowers && <col style={{ width: '70px' }} />}
          <col style={{ width: '70px' }} />
          <col style={{ width: '70px' }} />
          <col style={{ width: '36px' }} />
          <col style={{ width: '90px' }} />
        </colgroup>
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-1.5 py-2 text-left font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Power #
            </th>
            <th className="px-1.5 py-2 text-left font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Defendant
            </th>
            <th className="px-1.5 py-2 text-left font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Exec Date
            </th>
            <th className="px-1.5 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Amount
            </th>
            <th className="px-1.5 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Premium
            </th>
            {hasSCPowers && (
              <th className="px-1.5 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 uppercase">
                SC Prem
              </th>
            )}
            <th className="px-1.5 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 uppercase">
              NET
            </th>
            <th className="px-1.5 py-2 text-right font-semibold text-gray-600 dark:text-gray-400 uppercase">
              BUF
            </th>
            <th className="px-1 py-2 text-center font-semibold text-gray-600 dark:text-gray-400 uppercase">
              <CameraIcon className="h-4 w-4 mx-auto" title="Proof" />
            </th>
            <th className="px-1 py-2 text-center font-semibold text-gray-600 dark:text-gray-400 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
          {powers.map((power) => {
            const isLegacy = power.powerReportPowerId === 0;
            const canEdit = !isLegacy && !isSubmitted;

            return (
              <tr
                key={power.powerReportPowerId}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <td className="px-1.5 py-2 font-mono text-gray-900 dark:text-white">
                  <div className="flex items-center gap-1">
                    {power.powerNumber || 'N/A'}
                    {power.isRewrite && (
                      <span
                        className="inline-flex items-center text-xs font-bold text-amber-600 dark:text-amber-400"
                        title={`Rewrite of ${power.rewriteFromPowerNumber}${power.rewriteFromBondAmount ? ` ($${formatCurrency(power.rewriteFromBondAmount)})` : ''}`}
                      >
                        RW
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-1.5 py-2 text-gray-900 dark:text-white truncate" title={`${capitalizeFirst(power.defendantFirstName)} ${capitalizeFirst(power.defendantLastName)}`}>
                  {capitalizeFirst(power.defendantFirstName)} {capitalizeFirst(power.defendantLastName)}
                </td>
                <td className="px-1.5 py-2 text-gray-600 dark:text-gray-400">
                  {formatDate(power.executionDate)}
                </td>
                <td className="px-1.5 py-2 text-gray-900 dark:text-white text-right">
                  ${formatCurrency(power.powerAmount)}
                </td>
                <td className="px-1.5 py-2 text-gray-900 dark:text-white text-right">
                  ${formatCurrency(power.premium)}
                </td>
                {hasSCPowers && (
                  <td className="px-1.5 py-2 text-gray-900 dark:text-white text-right">
                    ${formatCurrency(power.scamount)}
                  </td>
                )}
                <td className="px-1.5 py-2 text-gray-900 dark:text-white text-right">
                  {power.isRewrite ? (
                    <span
                      className="text-amber-600 dark:text-amber-400"
                      title={`Rewrite - Due: $${formatCurrency(power.netDueAmount ?? 0)}`}
                    >
                      ${formatCurrency(power.commission)}
                    </span>
                  ) : (
                    `$${formatCurrency(power.commission)}`
                  )}
                </td>
                <td className="px-1.5 py-2 text-gray-900 dark:text-white text-right">
                  {power.isRewrite ? (
                    <span
                      className="text-amber-600 dark:text-amber-400"
                      title={`Rewrite - Due: $${formatCurrency(power.bufDueAmount ?? 0)}`}
                    >
                      ${formatCurrency(power.buf)}
                    </span>
                  ) : (
                    `$${formatCurrency(power.buf)}`
                  )}
                </td>
                {/* Proof Column */}
                <td className="px-1 py-2 text-center">
                  {isLegacy ? (
                    <span className="text-gray-300 dark:text-gray-600" title="Legacy power">
                      <CameraIcon className="h-4 w-4 mx-auto" />
                    </span>
                  ) : (
                    <button
                      onClick={() => onUploadProof && onUploadProof(power)}
                      className={`p-0.5 rounded transition-colors ${
                        power.hasProof
                          ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      title={power.hasProof ? 'View/Update Proof' : 'Upload Proof'}
                    >
                      {power.hasProof ? (
                        <div className="relative">
                          <CameraIcon className="h-4 w-4" />
                          <CheckCircleIcon className="h-2.5 w-2.5 absolute -bottom-0.5 -right-0.5 text-green-600 dark:text-green-400" />
                        </div>
                      ) : (
                        <CameraIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </td>
                {/* Actions Column */}
                <td className="px-1 py-2">
                  <div className="flex items-center justify-center gap-0.5">
                    {canEdit ? (
                      <>
                        <button
                          onClick={() => onEditPower(power)}
                          className="p-1 text-primary dark:text-primary-light hover:bg-primary/10 rounded transition-colors"
                          title="Edit Power"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDeletePower(power.powerReportPowerId)}
                          className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Delete Power"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <span className="p-1 text-gray-300 dark:text-gray-600" title={isSubmitted ? "Report submitted" : "Legacy power"}>
                        <PencilIcon className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        {/* Totals Row */}
        <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
          <tr>
            <td colSpan={3} className="px-1.5 py-2 font-bold text-gray-900 dark:text-white">
              TOTALS ({powers.length} {powers.length === 1 ? 'Power' : 'Powers'})
            </td>
            <td className="px-1.5 py-2 font-bold text-gray-900 dark:text-white text-right">
              ${formatCurrency(calculateTotal('powerAmount'))}
            </td>
            <td className="px-1.5 py-2 font-bold text-gray-900 dark:text-white text-right">
              ${formatCurrency(calculateTotal('premium'))}
            </td>
            {hasSCPowers && (
              <td className="px-1.5 py-2 font-bold text-gray-900 dark:text-white text-right">
                ${formatCurrency(calculateTotal('scamount'))}
              </td>
            )}
            <td className="px-1.5 py-2 font-bold text-primary dark:text-primary-light text-right">
              ${formatCurrency(calculateNetTotal())}
            </td>
            <td className="px-1.5 py-2 font-bold text-primary dark:text-primary-light text-right">
              ${formatCurrency(calculateBufTotal())}
            </td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default PowersTable;
